import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Container,
  Navbar,
  NavbarBrand,
  NavItem,
  NavLink,
} from 'reactstrap';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import fireIcon from '../images/fire.svg';
import logoutIcon from '../images/logout.png';
import * as AppConstants from '../AppConstants';
import moment from 'moment';
import { Bell } from 'react-feather';
import {
  fetchNotificationSummary,
  playNotificationChime,
} from '../notificationUtils';
import { NotificationDetailsModal } from './modals/NotificationDetailsModal';

const NotificationToastContent = ({ notification, onOpen }) => (
  <button
    type="button"
    className="notification-toast-content"
    onClick={onOpen}
  >
    <span className="notification-toast-icon" aria-hidden="true">
      <Bell size={18} />
    </span>
    <span className="notification-toast-copy">
      <span className="notification-toast-title">
        {notification.title}
      </span>
      <span className="notification-toast-message">
        {notification.message}
      </span>
    </span>
  </button>
);

export const NavMenu = () => {
  const [selectedNotification, setSelectedNotification] = useState(null);
  const baselineReady = useRef(false);
  const seenNotificationIds = useRef(new Set());

  const logout = (e) => {
    e.preventDefault();

    //Destroy session info
    localStorage.clear();

    //Reload window, Routing will handle redirect
    window.location.reload();
  };

  // Define a function that shows an alert
  const logutInactivity = () => {
    let lastInteraction = moment(localStorage.getItem('lastInteraction')); //Last user interaction datetime
    let timeDiff = moment().diff(lastInteraction, 'minutes'); //Difference between last interaction and current datetime
    if (timeDiff > 60) {
      //Logout due to inactivity
      logout(new Event('click'));
    } else {
      //Update last interaction
      localStorage.setItem('lastInteraction', moment());
    }
  };

  // Use useEffect to add an event listener to the window object
  useEffect(() => {
    // Add the event listener on mount
    window.addEventListener('click', logutInactivity);

    // Remove the event listener on unmount
    return () => {
      window.removeEventListener('click', logutInactivity);
    };
  }, []); // Pass an empty dependency array to run only once

  const openNotification = useCallback((notification) => {
    setSelectedNotification(notification);
  }, []);

  const showNotificationToast = useCallback(
    (notification) => {
      const toastId = `notification-${notification.id}`;

      if (toast.isActive(toastId)) {
        return;
      }

      toast.info(
        <NotificationToastContent
          notification={notification}
          onOpen={() => {
            toast.dismiss(toastId);
            openNotification(notification);
          }}
        />,
        {
          toastId,
          position: 'top-right',
          autoClose: false,
          closeOnClick: false,
          closeButton: true,
          draggable: false,
          className: 'notification-toast-shell',
          bodyClassName: 'notification-toast-body',
        },
      );
    },
    [openNotification],
  );

  const { data: notificationSummary, isFetching: isFetchingSummary } =
    useQuery('notificationSummary', fetchNotificationSummary, {
      enabled: localStorage.getItem('userId') !== null,
      refetchInterval: 60000,
      refetchOnWindowFocus: false,
      onSuccess: (summary) => {
        const latest = summary?.latestUnreadNotifications || [];

        if (!baselineReady.current) {
          latest.forEach((notification) => {
            seenNotificationIds.current.add(notification.id);
          });
          baselineReady.current = true;
          return;
        }

        const newNotifications = latest.filter(
          (notification) =>
            !seenNotificationIds.current.has(notification.id),
        );

        if (newNotifications.length > 0) {
          newNotifications.forEach(showNotificationToast);
          playNotificationChime();
        }

        latest.forEach((notification) => {
          seenNotificationIds.current.add(notification.id);
        });
      },
    });

  const unreadCount = notificationSummary?.unreadCount ?? 0;

  return (
    <header>
      <Navbar
        className="navbar ng-white border-bottom box-shadow mb-3"
        light
      >
        <Container fluid={false}>
          <div className="d-flex justify-content-between align-items-center w-100 nav-shell">
            <div className="d-flex align-items-center nav-left">
              <NavbarBrand tag={Link} to="/home" className="mb-0">
                <img
                  src={AppConstants.BASE_URL_FILE + 'AppShell/squarelogo.png'}
                  alt="Logo"
                  width={64}
                  height={64}
                  style={{ objectFit: 'contain' }}
                />
              </NavbarBrand>
              <div className="nav-welcome d-none d-md-block ml-2">
                Welcome, {localStorage.getItem('username')?.toUpperCase()}
              </div>
            </div>
            <div className="d-flex flex-row nav-actions">
              <ul className="navbar-nav flex-grow d-flex flex-row justify-content-center align-items-center ">
                <NavItem className="nav-welcome mr-2 d-md-none">
                  Welcome, {localStorage.getItem('username')?.toUpperCase()}
                </NavItem>
                <NavItem className="mr-3">
                  <NavLink
                    tag={Link}
                    className="text-dark p-0"
                    to="/notifications"
                    aria-label={`Unread notifications: ${unreadCount}`}
                  >
                    <div className="nav-badge-icon d-flex align-items-center justify-content-center">
                      <Bell size={18} />
                      <span className="nav-badge-count">
                        {isFetchingSummary && !notificationSummary
                          ? '...'
                          : unreadCount}
                      </span>
                    </div>
                  </NavLink>
                </NavItem>
                <NavItem className="mr-3">
                  <NavLink
                    tag={Link}
                    className="text-dark p-0"
                    to="/HotProperties"
                  >
                    <div className="nav-icon-button" title="Hot Properties" aria-label="Hot Properties">
                      <img
                        className="nav-icon-image"
                        src={fireIcon}
                        alt="Hot Properties"
                      />
                    </div>
                  </NavLink>
                </NavItem>
                <NavItem className="mr-3">
                  <NavLink
                    tag={Link}
                    className="text-dark p-0"
                    to="/Favourites"
                  >
                    <div className="nav-icon-button" title="Favourite Properties" aria-label="Favourite Properties">
                      <i className="far fa-heart nav-icon-heart" aria-hidden="true"></i>
                    </div>
                  </NavLink>
                </NavItem>
                <NavItem className="mr-2">
                  <NavLink tag={Link} className="text-dark p-0" to="#">
                    <div className="nav-icon-button" title="Logout" aria-label="Logout">
                      <img
                        className="nav-icon-image"
                        src={logoutIcon}
                        alt="Logout"
                        onClick={(e) => {
                          logout(e);
                        }}
                      />
                    </div>
                  </NavLink>
                </NavItem>
              </ul>
            </div>
          </div>
        </Container>
      </Navbar>
      <NotificationDetailsModal
        show={Boolean(selectedNotification)}
        notification={selectedNotification}
        onClose={() => setSelectedNotification(null)}
        onMarkedRead={(notification) =>
          setSelectedNotification(notification)
        }
      />
    </header>
  );
};
